import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom"; // Import useParams from React Router
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Product } from "types";
import DataTable from "react-data-table-component";
import { Order, Agent, Address, Addresses } from "types";
import { getAuthCookie } from "../../utils/authCookies";

interface AgentDetails {
  brokerageFirmAffiliation: string;
  managingBroker: string;
  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;
}

interface FirmInfo {
  companyID: string;
  companyName: string;
  address: Address;
  specialities: string;
  regionsServed: string;
}

type AgentProduct = {
  product_id: string;
  agent_id: string;
  product: Product;
};

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageBrokerageViewAgent = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [agent, setAgent] = useState<Agent | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [agentProducts, setAgentProducts] = useState<AgentProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<AgentProduct[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });
  const navigate = useNavigate();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Fetch the office data based on officeId
  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${agentId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch agent data");
        }
        const data = await response.json();
        if (data && data.firmDetails && data.firmDetails.address) {
          setAgent(data);

          // setUserModules(data.modulePermissions);
          // console.log(userModules);
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching agent data:", error);
      }
    };

    if (agentId) {
      fetchAgentData();
    }
  }, [agentId]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user-addresses/${agentId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        console.log(addresses);
        // setAddresses(data.addresses);
        setAddressObjects(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token]);

  // if (addresses?.length === 0) {
  //   return <div>Loading...</div>;
  // }

  useEffect(() => {
    if (addresses !== null && addresses?.length > 0) {
      const table = $("#transactions").DataTable({
        responsive: false,
        destroy: true,
        lengthChange: false,
        ordering: false,
      });

      return () => {
        table.destroy();
      };
    }
  }, [addresses]);

  const handleDelete = async (brandId: string) => {
    setDeleting(brandId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses/${brandId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the brand.");
      }

      toast.success("Brand deleted successfully!");
      // Update the state by removing the deleted license
      setAddressObjects((prev) =>
        prev?.filter((group) => group._id !== brandId)
      );
      setDeleting(null); // Clear the deleting state
      // navigate(0);
    } catch (err) {
      toast.error("Failed to delete the brand.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null); // Clear the deleting state
    }
  };

  // if (!agent) return <div>Loading Agent Data...</div>;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // userFirstName: userInfo?.firstName || "",
      // userLastName: userInfo?.lastName || "",
      // userEmail: userInfo?.email || "",
      // userPhone: userInfo?.phone1 || "",
      label_of_address: "",
      firstName: "",
      lastName: "",
      company: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      county: "",
      state: "",
      zipcode: "",
      phone: "",
    },
    validationSchema: Yup.object({
      userFirstName: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
    }),
    onSubmit: async (values) => { },
  });

  const handleShowAddAddress = () => {
    setShowAddressForm(true);
  };

  const handleAddAddress = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    try {
      // const token = getAuthCookie("sessionToken");
      // const userId = getAuthCookie("userId");
      console.log("Adding address:", formik.values);

      if (!agentId || !token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: agentId,
            user_type: "agent",
            label: formik.values.label_of_address,
            first_name: formik.values.firstName,
            last_name: formik.values.lastName,
            company: formik.values.company,
            address_details: {
              label: formik.values.label_of_address,
              addressLine1: formik.values.addressLine1,
              addressLine2: formik.values.addressLine2,
              city: formik.values.county,
              state: formik.values.state,
              zip: formik.values.zipcode,
              country: formik.values.country,
              phone: formik.values.phone,
            },
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to add address");

      const data = await response.json();
      setAddressObjects([...addressObjects, data]);
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log("Place changed:", place);

      const addressComponents = place.address_components;
      console.log(addressComponents);

      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();

        console.log("Latitude:", latitude, "Longitude:", longitude);

        // Update form values
        formik.setFieldValue('addressLine1', street);
        formik.setFieldValue('city', cityObj.long_name);
        formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        formik.setFieldValue('state', stateObj.long_name);
        formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        formik.setFieldValue('zipcode', postalCode);
        formik.setFieldValue('country', countryObj.long_name);
        formik.setFieldValue('countryCode', countryObj.short_name);

        if (latitude && longitude) {
          formik.setFieldValue('latitude', latitude);
          formik.setFieldValue('longitude', longitude);
        }
      }
    }
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('cityCode', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('stateCode', '', false);
    formik.setFieldValue('zipcode', '', false);
    formik.setFieldValue('country', '', false);
    formik.setFieldValue('countryCode', '', false);
    formik.setFieldValue('latitude', '', false);
    formik.setFieldValue('longitude', '', false);
  };

  const handleAddressLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    if (!e.target.value.trim()) {
      clearGoogleAddressFields();
    }
  };

  const fetchProducts = async (page: number, limit: number) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/products/paginated?page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch products.");
      }

      const data = await response.json();
      setAgentProducts(data.data || []);
      setFilteredProducts(data.data || []); // Initialize filtered products
      setTotalRows(data.recordsFiltered || data.recordsTotal || 0);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error fetching data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage, rowsPerPage); // Fetch data on initial load
  }, [currentPage, rowsPerPage]);

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    // Filter products based on the search term
    const filtered = agentProducts?.filter(
      (product) =>
        product.product.name.toLowerCase().includes(value) ||
        product.product.category.toLowerCase().includes(value) ||
        product.product.status.toLowerCase().includes(value)
    );
    setFilteredProducts(filtered);
  };

  const columns = [
    {
      name: "S.No.",
      cell: (row: AgentProduct, index: number) =>
        (currentPage - 1) * rowsPerPage + index + 1,
      width: "70px",
    },
    {
      name: "Image",
      cell: (row: AgentProduct) =>
        row.product.image?.url ? (
          <img
            src={`${process.env.REACT_APP_BASE_URL}${row.product.image.url}`}
            alt="Product"
            height={50}
          />
        ) : (
          "No Image"
        ),
    },
    {
      name: "Product Name",
      selector: (row: AgentProduct) => row.product.name,
      sortable: true,
    },
    {
      name: "Category",
      selector: (row: AgentProduct) => row.product.category,
      sortable: true,
    },
    {
      name: "Qty",
      selector: (row: AgentProduct) =>
        row.product?.variants[0]?.inventory[0]?.on_hand || 0,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row: AgentProduct) => row.product.status,
      sortable: true,
    },
    {
      name: "Condition",
      selector: (row: AgentProduct) => row.product.item_condition,
      sortable: true,
    },
    {
      name: "Dimensions",
      cell: (row: AgentProduct) =>
        `${row.product.package_dimensions?.length} x ${row.product.package_dimensions.width} x ${row.product.package_dimensions.height}`,
    },
    {
      name: "Linked Installer",
      selector: (row: AgentProduct) =>
        row.product.linked_installer || "None",
    },
    {
      name: "Actions",
      cell: (row: AgentProduct) => (
        <div>
          <Link
            to={`/realtor/viewProduct/${row.product_id}`}
            className="btn btn-info btn-sm me-2"
          >
            View
          </Link>
          <Link
            to={`/realtor/editProduct/${row.product_id}`}
            className="btn btn-warning btn-sm me-2"
          >
            Edit
          </Link>
          <button
            onClick={() => handleDelete(row.product_id)}
            className="btn btn-danger btn-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchProducts(page, rowsPerPage);
  };

  const handleRowsPerPageChange = (newPerPage: number, page: number) => {
    setRowsPerPage(newPerPage);
    fetchProducts(page, newPerPage);
  };

  // Fetch orders data
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/agent/${agentId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch orders data");
        }

        const data = await response.json();
        console.log("Fetched orders Data:", data);
        if (data) {
          setOrders(data);
        }
      } catch (err) {
        console.error("Error fetching orders data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchOrders();
  }, [userId, token]);

  return (
    <div className="realtor">
      <div className="card">
        <div className="card-body">
          <div className="profile-foreground position-relative mx-n4 mt-n4">
            <div className="profile-wid-bg">
              <img
                src="/assets/images/profile-bg.jpg"
                alt=""
                className="profile-wid-img"
              />
            </div>
          </div>
          <div className="pt-4 mb-4 mb-lg-3 pb-lg-4 profile-wrapper">
            <div className="row g-4">
              <div className="col-auto">
                <div className="avatar-lg">
                  <img
                    src="/assets/images/users/avatar-1.jpg"
                    alt="user-img"
                    className="img-thumbnail rounded-circle"
                  />
                </div>
              </div>

              <div className="col">
                <div className="p-2">
                  <h3 className="text-white mb-1">
                    {agent?.firstName} {agent?.lastName}
                  </h3>
                  <p className="text-white text-opacity-75">Agent</p>
                </div>
              </div>
              <div className="col-12 col-lg-auto order-last order-lg-0"></div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div>
                <div className="tab-content pt-4 text-muted">
                  <div
                    className="tab-pane active"
                    id="overview-tab"
                    role="tabpanel"
                  >
                    <div className="row">
                      <div className="col-md-12">
                        <ul className="nav nav-tabs" id="myTab" role="tablist">
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link active"
                              id="profile-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#profile-tab-pane"
                              type="button"
                              role="tab"
                              aria-controls="profile-tab-pane"
                              aria-selected="false"
                            >
                              Profile
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="addresses-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#addresses-tab-pane"
                              type="button"
                              role="tab"
                              aria-controls="addresses-tab-pane"
                              aria-selected="false"
                            >
                              Addresses
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="inventory-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#inventory-tab-pane"
                              type="button"
                              role="tab"
                              aria-controls="inventory-tab-pane"
                              aria-selected="true"
                            >
                              Inventory
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="orders-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#orders-tab-pane"
                              type="button"
                              role="tab"
                              aria-controls="orders-tab-pane"
                              aria-selected="true"
                            >
                              Orders
                            </button>
                          </li>
                        </ul>
                        <div className="tab-content" id="myTabContent">
                          <div
                            className="tab-pane fade show active"
                            id="profile-tab-pane"
                            role="tabpanel"
                            aria-labelledby="profile-tab"
                            tabIndex={0}
                          >
                            <div className="card">
                              <div className="card-body">
                                <h5 className="card-title mb-3">Profile</h5>
                                <div className="table-responsive">
                                  <table className="table table-borderless mb-0">
                                    <tbody>
                                      <tr>
                                        <th scope="row">Full Name :</th>
                                        <td className="text-muted">
                                          {agent?.firstName} {agent?.lastName}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Role :</th>
                                        <td className="text-muted">Agent</td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Phone :</th>
                                        <td className="text-muted">
                                          {agent?.phone1}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">E-mail :</th>
                                        <td className="text-muted">
                                          {agent?.email}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Location :</th>
                                        <td className="text-muted">
                                          {
                                            agent?.firmDetails.address
                                              .addressLine1
                                          }
                                          ,
                                          {
                                            agent?.firmDetails.address
                                              .addressLine2
                                          }
                                          ,{agent?.firmDetails.address.city},
                                          {agent?.firmDetails.address.state},
                                          {agent?.firmDetails.address.zip}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">
                                          Brokerage Firm Affiliation :
                                        </th>
                                        <td className="text-muted">
                                          {
                                            agent?.agentDetails
                                              .brokerageFirmAffiliation
                                          }
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Agent ID/Code :</th>
                                        <td className="text-muted">
                                          {agent?.agentDetails.agentID}
                                        </td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Status :</th>
                                        <td className="text-muted">
                                          {agent?.status}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            className="tab-pane fade"
                            id="addresses-tab-pane"
                            role="tabpanel"
                            aria-labelledby="addresses-tab"
                            tabIndex={0}
                          >
                            <div className="card mt-4 card-bg-fill">
                              <div className="card-body p-4">
                                <div className="table-container">
                                  <table
                                    id="transactions"
                                    className="table table-bordered dt-responsive nowrap table-striped align-middle w-100"
                                  >
                                    <thead>
                                      <tr>
                                        <th>S.No.</th>
                                        <th>Label of address </th>
                                        <th>Address line 1</th>
                                        <th>City</th>
                                        <th>State</th>
                                        <th>Country</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {addressObjects != null &&
                                        addressObjects?.map((address, index) => (
                                          <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{address?.label}</td>
                                            <td>
                                              {address?.address_details?.addressLine1}
                                            </td>
                                            <td>{address?.address_details?.city}</td>
                                            <td>{address?.address_details?.state}</td>
                                            <td>{address?.address_details?.country}</td>
                                            <td>
                                              <Link
                                                to="#"
                                                className="btn btn-warning waves-effect waves-light me-2"
                                              >
                                                <i className="ri-pencil-fill align-bottom me-2"></i>{" "}
                                              </Link>

                                              <button
                                                onClick={() =>
                                                  window.confirm(
                                                    "Are you sure you want to delete this address?"
                                                  ) && handleDelete(address._id)
                                                }
                                                className="btn btn-danger waves-effect waves-light"
                                              >
                                                <i className="ri-delete-bin-fill align-bottom me-2"></i>
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="row mt-3">
                                  <div className="col-md-3">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success mb-3"
                                      onClick={handleShowAddAddress}
                                    >
                                      Add Address
                                    </button>
                                  </div>
                                </div>

                                {showAddressForm && (
                                  <div className="row" id="addressForm">
                                    <form>
                                      <div className="row">
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Address Label
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="label_of_address"
                                              name="label_of_address"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={
                                                formik.values.label_of_address
                                              }
                                            />
                                            {formik.touched.label_of_address &&
                                              formik.errors.label_of_address ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.label_of_address}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              First Name
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="firstName"
                                              name="firstName"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.firstName}
                                            />
                                            {formik.touched.firstName &&
                                              formik.errors.firstName ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.firstName}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Last Name
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="lastName"
                                              name="lastName"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.lastName}
                                            />
                                            {formik.touched.lastName &&
                                              formik.errors.lastName ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.lastName}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Company
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="company"
                                              name="company"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.company}
                                            />
                                            {formik.touched.company &&
                                              formik.errors.company ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.company}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Address Line 1
                                            </label>
                                            {
                                              isLoaded &&
                                              <Autocomplete
                                                onLoad={(autocomplete) =>
                                                (autocompleteRef.current =
                                                  autocomplete)
                                                }
                                                onPlaceChanged={
                                                  handlePlaceChanged
                                                }
                                              >
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  id="addressLine1"
                                                  name="addressLine1"
                                                  onChange={handleAddressLine1Change}
                                                  onBlur={formik.handleBlur}
                                                  value={
                                                    formik.values.addressLine1
                                                  }
                                                  required
                                                />
                                              </Autocomplete>
                                            }
                                            {formik.touched.addressLine1 &&
                                              formik.errors.addressLine1 ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.addressLine1}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Address Line 2
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="addressLine2"
                                              name="addressLine2"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.addressLine2}
                                            />
                                            {formik.touched.addressLine2 &&
                                              formik.errors.addressLine2 ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.addressLine2}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              County
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="county"
                                              name="county"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.county}
                                            />
                                            {formik.touched.county &&
                                              formik.errors.county ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.county}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              State
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="state"
                                              name="state"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.state}
                                              disabled
                                            />
                                            {formik.touched.state &&
                                              formik.errors.state ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.state}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Zip Code
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="zipcode"
                                              name="zipcode"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.zipcode}
                                              disabled
                                            />
                                            {formik.touched.zipcode &&
                                              formik.errors.zipcode ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.zipcode}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-3">
                                            <label className="form-label">
                                              Country
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control"
                                              id="country"
                                              name="country"
                                              onChange={formik.handleChange}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.country}
                                              disabled
                                            />
                                            {formik.touched.country &&
                                              formik.errors.country ? (
                                              <div className="invalid-feedback">
                                                {formik.errors.country}
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="modal-footer">
                                        <button
                                          type="button"
                                          onClick={(e) => handleAddAddress(e)}
                                          className="btn btn-primary"
                                        >
                                          Save Address
                                        </button>
                                      </div>
                                    </form>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div
                            className="tab-pane fade"
                            id="inventory-tab-pane"
                            role="tabpanel"
                            aria-labelledby="inventory-tab"
                            tabIndex={0}
                          >
                            <div className="d-flex justify-content-between mb-3 mt-3">
                              {/* <div>
                                <Link to="/realtor/addProduct" className="btn btn-primary">
                                  Add Inventory Item
                                </Link>
                              </div> */}
                              <div>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search..."
                                  value={searchTerm}
                                  onChange={handleSearch}
                                />
                              </div>
                            </div>
                            <DataTable
                              columns={columns}
                              data={filteredProducts} // Use filtered products for the table
                              progressPending={loading}
                              pagination
                              paginationServer
                              paginationTotalRows={totalRows}
                              onChangePage={handlePageChange}
                              onChangeRowsPerPage={handleRowsPerPageChange}
                              highlightOnHover
                              striped
                              responsive
                            />
                          </div>
                          <div
                            className="tab-pane fade"
                            id="orders-tab-pane"
                            role="tabpanel"
                            aria-labelledby="orders-tab"
                            tabIndex={0}
                          >
                            <div className="card mt-4 card-bg-fill">
                              <div className="card-body p-4">
                                <div className="table-responsive table-card mb-1">
                                  <table
                                    className="table table-nowrap align-middle"
                                    id="orderTable"
                                  >
                                    <thead className="text-muted table-light">
                                      <tr className="text-uppercase">
                                        <th scope="col" style={{ width: "25px" }}>
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id="checkAll"
                                              value="option"
                                            />
                                          </div>
                                        </th>
                                        <th>
                                          Order ID
                                        </th>
                                        <th>
                                          Customer
                                        </th>
                                        <th>
                                          Product
                                        </th>
                                        <th>
                                          Order Date
                                        </th>
                                        <th>
                                          Amount
                                        </th>
                                        <th>
                                          Payment Method
                                        </th>
                                        <th>
                                          Delivery Status
                                        </th>
                                        <th>
                                          Action
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="list form-check-all">
                                      {orders != null && orders?.map((order) => (
                                        <tr>
                                          <th scope="row">
                                            <div className="form-check">
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="checkAll"
                                                value="option1"
                                              />
                                            </div>
                                          </th>
                                          <td className="id">
                                            <a
                                              href="apps-ecommerce-order-details.html"
                                              className="fw-medium link-primary"
                                            >
                                              {order.order_number}
                                            </a>
                                          </td>
                                          <td className="customer_name">{order.customer_name} </td>
                                          <td className="product_name">{order.items[0].product_name}</td>
                                          <td className="date">
                                            {/* {order.created_at} */}
                                            {/* <small className="text-muted">02:21 AM</small> */}
                                          </td>
                                          <td className="amount">{order.total_amount}</td>
                                          <td className="payment">{order.payment_method}</td>
                                          <td className="status">
                                            <span className="badge bg-warning-subtle text-warning text-uppercase">
                                              Pending
                                            </span>
                                          </td>
                                          <td>
                                            <ul className="list-inline hstack gap-2 mb-0">
                                              <li
                                                className="list-inline-item"
                                                data-bs-toggle="tooltip"
                                                data-bs-trigger="hover"
                                                data-bs-placement="top"
                                                title="View"
                                              >
                                                <a
                                                  href={`/agent/orderDetails/${order.id}`}
                                                  className="text-primary d-inline-block"
                                                >
                                                  <i className="ri-eye-fill fs-16"></i>
                                                </a>
                                              </li>
                                              {/* <li
                              className="list-inline-item edit"
                              data-bs-toggle="tooltip"
                              data-bs-trigger="hover"
                              data-bs-placement="top"
                              title="Edit"
                            >
                              <a
                                href="#showModal"
                                data-bs-toggle="modal"
                                className="text-primary d-inline-block edit-item-btn"
                              >
                                <i className="ri-pencil-fill fs-16"></i>
                              </a>
                            </li> */}
                                              {/* <li
                              className="list-inline-item"
                              data-bs-toggle="tooltip"
                              data-bs-trigger="hover"
                              data-bs-placement="top"
                              title="Remove"
                            >
                              <a
                                className="text-danger d-inline-block remove-item-btn"
                                data-bs-toggle="modal"
                                href="#deleteOrder"
                              >
                                <i className="ri-delete-bin-5-fill fs-16"></i>
                              </a>
                            </li> */}
                                            </ul>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <div className="noresult" style={{ display: "none" }}>
                                    <div className="text-center">
                                      {/* <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop" colors="primary:#405189,secondary:#0ab39c" style="width:75px;height:75px"></lord-icon> */}
                                      <h5 className="mt-2">Sorry! No Result Found</h5>
                                      <p className="text-muted">
                                        We've searched more than 150+ Orders We did not find any
                                        orders for you search.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* <div className="row">
                      <div className="col-xxl-3">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title mb-3">Profile</h5>
                            <div className="table-responsive">
                              <table className="table table-borderless mb-0">
                                <tbody>
                                  <tr>
                                    <th scope="row">Full Name :</th>
                                    <td className="text-muted">
                                      {agent.firstName} {agent.lastName}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Role :</th>
                                    <td className="text-muted">Agent</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Phone :</th>
                                    <td className="text-muted">
                                      {agent.phone1}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">E-mail :</th>
                                    <td className="text-muted">
                                      {agent.email}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Location :</th>
                                    <td className="text-muted">
                                      {agent.firmDetails.address.addressLine1},
                                      {agent.firmDetails.address.addressLine2},
                                      {agent.firmDetails.address.city},
                                      {agent.firmDetails.address.state},
                                      {agent.firmDetails.address.zip}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">
                                      Brokerage Firm Affiliation :
                                    </th>
                                    <td className="text-muted">
                                      {
                                        agent.agentDetails
                                          .brokerageFirmAffiliation
                                      }
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Agent ID/Code :</th>
                                    <td className="text-muted">
                                      {agent.agentDetails.agentID}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Status :</th>
                                    <td className="text-muted">
                                      {agent.status}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageBrokerageViewAgent;
